<template>
  <div class="row g-4 mb-4">

    <!-- En-tête -->
    <div class="col-12">
      <div class="card border-0 mb-0">
        <div class="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h4 class="text-black fw-bold mb-1">Vente {{ vente?.refCom || '' }}</h4>
            <div class="d-flex flex-wrap align-items-center gap-2 text-muted">
              <span>{{ format_Date(vente?.dateCommande) || '-' }}</span>
              <span v-if="statutPaiement" class="badge" :class="statutPaiement.classe">{{ statutPaiement.libelle }}</span>
              <span v-if="vente" class="badge" :class="estNormalisee ? 'bg-success' : 'bg-secondary'">
                {{ estNormalisee ? 'Normalisée' : 'Non normalisée' }}
              </span>
            </div>
          </div>
          <router-link to="/ventes/liste-ventes" class="btn btn-primary transition border-0 lh-1 fw-medium">
            <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
            Retour
          </router-link>
        </div>
      </div>
    </div>

    <!-- Vente -->
    <div class="col-lg-7">
      <div class="card border-0 h-100 mb-0">
        <div class="card-body">
          <h5 class="fw-bold mb-3">Informations de la vente</h5>
          <div class="row g-3">
            <div class="col-sm-6 col-md-4">
              <div class="info-label">Référence</div>
              <div class="info-value">{{ vente?.refCom || '-' }}</div>
            </div>
            <div class="col-sm-6 col-md-4">
              <div class="info-label">Date</div>
              <div class="info-value">{{ format_Date(vente?.dateCommande) || '-' }}</div>
            </div>
            <div class="col-sm-6 col-md-4">
              <div class="info-label">Type de commande</div>
              <div class="info-value">{{ vente?.typeCommande || '-' }}</div>
            </div>
            <div class="col-sm-6 col-md-4">
              <div class="info-label">Type de facture</div>
              <div class="info-value">{{ vente?.typeFacture || '-' }}</div>
            </div>
            <div class="col-sm-6 col-md-4">
              <div class="info-label">Taux AIB</div>
              <div class="info-value">{{ vente?.tauxAib ? `${vente.tauxAib} %` : 'Aucun' }}</div>
            </div>
            <div class="col-sm-6 col-md-4" v-if="vente?.dateLivPrev">
              <div class="info-label">Livraison prévue</div>
              <div class="info-value">{{ format_Date(vente?.dateLivPrev) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Client -->
    <div class="col-lg-5">
      <div class="card border-0 h-100 mb-0">
        <div class="card-body">
          <h5 class="fw-bold mb-3">Client</h5>
          <div class="row g-3">
            <div class="col-12">
              <div class="info-label">Nom</div>
              <div class="info-value">{{ nomClient }}</div>
            </div>
            <div class="col-sm-6">
              <div class="info-label">IFU</div>
              <div class="info-value">{{ vente?.client?.ifu || '-' }}</div>
            </div>
            <div class="col-sm-6">
              <div class="info-label">Téléphone</div>
              <div class="info-value">{{ vente?.client?.telephone1 || '-' }}</div>
            </div>
            <div class="col-12">
              <div class="info-label">Adresse</div>
              <div class="info-value">{{ vente?.client?.adresseClient || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Produits -->
    <div class="col-12">
      <div class="card border-0 mb-0">
        <div class="card-body">
          <h5 class="fw-bold mb-3">Produits vendus</h5>
          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th class="text-end">Qté</th>
                  <th class="text-end">Prix unit. TTC</th>
                  <th class="text-end">Remise</th>
                  <th class="text-end">Montant HT</th>
                  <th class="text-end">TVA</th>
                  <th class="text-end">Taxe spéc.</th>
                  <th class="text-end">Montant TTC</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(produit, index) in lignes" :key="index">
                  <td>
                    <div class="fw-medium">{{ produit?.libelleProduit || produit?.produitConditionnement?.nomProCond || '-' }}</div>
                    <small v-if="produit?.groupeTaxe" class="text-muted">Groupe de taxe {{ produit.groupeTaxe }}</small>
                  </td>
                  <td class="text-end">{{ nombre(produit?.qtite) }}</td>
                  <td class="text-end">{{ montant(produit?.prixTtc) }}</td>
                  <td class="text-end">{{ remiseLigne(produit) }}</td>
                  <td class="text-end">{{ montant(htLigne(produit)) }}</td>
                  <td class="text-end">{{ montant(produit?.montantTva) }}</td>
                  <td class="text-end">{{ montant(produit?.montantTs) }}</td>
                  <td class="text-end fw-bold">{{ montant(produit?.montantTtc) }}</td>
                </tr>
                <tr v-if="vente && !lignes.length">
                  <td colspan="8" class="text-center text-muted py-4">Aucun produit pour cette vente.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Normalisation -->
    <div class="col-lg-7">
      <div class="card border-0 h-100 mb-0">
        <div class="card-body">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 class="fw-bold mb-0">Normalisation MECeF</h5>
            <span v-if="vente" class="badge" :class="estNormalisee ? 'bg-success' : 'bg-secondary'">
              {{ estNormalisee ? 'Normalisée' : 'Non normalisée' }}
            </span>
          </div>

          <div class="row g-3">
            <div class="col-sm-6">
              <div class="info-label">Facture</div>
              <div class="info-value">{{ facture?.refFac || 'Pas encore générée' }}</div>
            </div>
            <div class="col-sm-6">
              <div class="info-label">Date facture</div>
              <div class="info-value">{{ format_Date(facture?.dateFac) || '-' }}</div>
            </div>
            <div class="col-12">
              <div class="info-label">Code MECeF/DGI</div>
              <div class="info-value text-break font-monospace">{{ facture?.mecefCode || '-' }}</div>
            </div>
            <div class="col-sm-4">
              <div class="info-label">Compteurs</div>
              <div class="info-value">{{ facture?.mecefCompteur || '-' }}</div>
            </div>
            <div class="col-sm-4">
              <div class="info-label">NIM</div>
              <div class="info-value">{{ facture?.mecefNim || '-' }}</div>
            </div>
            <div class="col-sm-4">
              <div class="info-label">Normalisée le</div>
              <div class="info-value">{{ format_Date(facture?.mecef_date) || '-' }}</div>
            </div>
          </div>

          <!-- Factures d'avoir ou factures supplémentaires liées à la vente -->
          <div v-if="autresFactures.length" class="mt-4">
            <div class="info-label mb-2">Autres factures liées</div>
            <div v-for="f in autresFactures" :key="f.id" class="d-flex flex-wrap justify-content-between gap-2 py-1">
              <span class="fw-medium">{{ f.refFac || '-' }} <small class="text-muted">{{ f.typeFacture || '' }}</small></span>
              <span class="font-monospace small">{{ f.mecefCode || 'Non normalisée' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Totaux -->
    <div class="col-lg-5">
      <div class="card border-0 h-100 mb-0">
        <div class="card-body">
          <h5 class="fw-bold mb-3">Récapitulatif</h5>
          <div class="total-ligne"><span>Total HT</span><span>{{ montant(totaux.ht) }}</span></div>
          <div class="total-ligne" v-if="totaux.remise > 0">
            <span>Remise</span><span>- {{ montant(totaux.remise) }}</span>
          </div>
          <div class="total-ligne"><span>TVA</span><span>{{ montant(totaux.tva) }}</span></div>
          <div class="total-ligne" v-if="totaux.ts > 0">
            <span>Taxe spécifique</span><span>{{ montant(totaux.ts) }}</span>
          </div>
          <div class="total-ligne" v-if="Number(vente?.montantAib) > 0">
            <span>AIB ({{ vente?.tauxAib }} %)</span><span>{{ montant(vente?.montantAib) }}</span>
          </div>
          <div class="total-ligne total-ttc"><span>Total TTC</span><span>{{ montant(totaux.ttc) }}</span></div>
          <div class="total-ligne"><span>Montant reçu</span><span>{{ montant(vente?.montantReçu) }}</span></div>
          <div class="total-ligne" :class="Number(vente?.montantRest) > 0 ? 'text-danger fw-bold' : ''">
            <span>Reste à payer</span><span>{{ montant(vente?.montantRest) }}</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import ApiService from "@/services/ApiService";
import { error, format_Date } from "@/utils/utils";
import { useRoute } from "vue-router";

const STATUTS_PAIEMENT: Record<string, { libelle: string; classe: string }> = {
  paye: { libelle: 'Payée', classe: 'bg-success' },
  partiel: { libelle: 'Paiement partiel', classe: 'bg-warning text-dark' },
  en_attente: { libelle: 'En attente de paiement', classe: 'bg-warning text-dark' },
  annule: { libelle: 'Annulée', classe: 'bg-danger' },
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export default defineComponent({
  name: "ViewVente",
  setup: () => {
    const route = useRoute();
    const vente = ref<any>(null);

    function getCommandeClient(id: string) {
      return ApiService.get(`/commandeClients/${id}`)
        .then(({ data }) => {
          vente.value = data.data;
        })
        .catch(({ response }) => {
          error(response.data.message);
        });
    }

    // Les décimaux arrivent de la base sous forme de chaîne ("100.00") : on les
    // convertit avant formatage, sinon toLocaleString ne s'applique pas.
    function nombre(valeur: unknown) {
      const n = Number(valeur);
      if (valeur === null || valeur === undefined || valeur === '' || isNaN(n)) return '-';
      return n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
    }

    function montant(valeur: unknown) {
      const n = nombre(valeur);
      return n === '-' ? '-' : `${n} FCFA`;
    }

    function remiseLigne(produit: any) {
      if (!(Number(produit?.remise) > 0)) return '-';
      if (produit?.typeRemise === '%') return `${montant(produit.remise)} (${nombre(produit.valeurRemise)} %)`;
      return montant(produit.remise);
    }

    function htLigne(produit: any) {
      return Number(produit?.montantHtApresRemise) || Number(produit?.montantHt) || 0;
    }

    const lignes = computed<any[]>(() => vente.value?.proCondiCommandeClients || []);

    // Totaux recalculés depuis les lignes : les totaux stockés sur la commande
    // (montHT notamment) ne sont pas toujours renseignés.
    const totaux = computed(() => {
      const somme = (fn: (p: any) => number) => lignes.value.reduce((t, p) => t + (fn(p) || 0), 0);
      return {
        ht: somme(htLigne),
        remise: somme((p) => Number(p?.remise)),
        tva: somme((p) => Number(p?.montantTva)),
        ts: somme((p) => Number(p?.montantTs)),
        ttc: somme((p) => Number(p?.montantTtc)),
      };
    });

    const nomClient = computed(() => {
      const client = vente.value?.client;
      if (!client) return '-';
      if (client.nomClient) return `${client.nomClient} ${client.prenomClient || ''}`.trim();
      return client.raisonSociale || '-';
    });

    const factures = computed<any[]>(() => vente.value?.commandeFactures || []);

    // Facture principale de la vente (hors factures d'avoir)
    const facture = computed<any>(() =>
      factures.value.find((f) => !f.factureOriginale && !/avoir/i.test(f.typeFacture || '')) || factures.value[0] || null
    );

    const autresFactures = computed<any[]>(() => factures.value.filter((f) => f !== facture.value));

    const estNormalisee = computed(() => !!facture.value?.estNormalisee || !!vente.value?.estNormalisee);

    const statutPaiement = computed(() => STATUTS_PAIEMENT[vente.value?.statutPaiement] || null);

    onMounted(() => {
      getCommandeClient(route.params.id as string);
    });

    return {
      vente,
      lignes,
      totaux,
      facture,
      autresFactures,
      nomClient,
      estNormalisee,
      statutPaiement,
      getCommandeClient,
      format_Date,
      nombre,
      montant,
      remiseLigne,
      htLigne,
    };
  },
});
</script>
<style scoped>
.info-label {
  font-size: 0.8rem;
  color: #8e8da2;
  margin-bottom: 2px;
}

.info-value {
  font-weight: 600;
  color: #2b2a3f;
}

.table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #8e8da2;
  white-space: nowrap;
}

.table td {
  white-space: nowrap;
}

.table td:first-child {
  white-space: normal;
  min-width: 180px;
}

.total-ligne {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px dashed #eee;
}

.total-ligne:last-child {
  border-bottom: none;
}

.total-ttc {
  font-weight: 700;
  font-size: 1.05rem;
  border-top: 2px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
}
</style>
